// components/ActivityTracker/ActivityTrackerPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Badge, Button, Dialog, DialogActions, DialogBody, DialogContent,
  DialogSurface, DialogTitle, DialogTrigger, Field, Input,
  Menu, MenuItem, MenuList, MenuPopover, MenuTrigger,
  Spinner, Text, Tooltip,
} from "@fluentui/react-components";
import {
  Add24Regular, ArrowSync24Regular, CheckmarkCircle24Filled,
  Edit24Regular, Archive24Regular, ArrowCounterclockwise24Regular,
  Grid24Regular, List24Regular, Search24Regular, TaskListSquareLtr24Regular,
  Warning24Regular, Eye24Regular, DocumentArrowDown24Regular,
  MailMultiple24Regular, Clock24Regular,
} from "@fluentui/react-icons";
import { useActivityTracker } from "../../hooks/useActivityTracker";
import { useUserAccess } from "../../context/UserAccessContext";
import { useDeptOwnerMap } from "../../hooks/useDeptOwnerMap";
import ActivityForm, { PRIORITIES, STATUSES, CATEGORIES } from "./ActivityForm";
import ActivityDetailPanel from "./ActivityDetailPanel";
import { sendGMReport, sendUserReports } from "../../services/reportService";
import { useMsal } from "@azure/msal-react";
import PeoplePicker from "../shared/PeoplePicker";
import {
  finalStatusOf, wasCompletedAtClose, latestProgressNote,
  hasUnrecordedOutcome, outcomeBackfillEntry,
} from "../../services/activityService";
import {
  usePagination,
  Pagination as TablePagination,
  useDebounced,
} from "../common/Pagination";

// ── Design tokens ──────────────────────────────────────────────────────────────
const PRIORITY_STYLE = {
  High:   { bg: "#FDE7E9", color: "#C4262E", dot: "#C4262E" },
  Medium: { bg: "#FFF4EC", color: "#D1521A", dot: "#D1521A" },
  Low:    { bg: "#EAF6EA", color: "#217A21", dot: "#217A21" },
};
const STATUS_STYLE = {
  "Not Started": { bg: "#F3F2F1", color: "#605E5C" },
  "In Progress": { bg: "#EBF3FF", color: "#0052A3" },
  "On Hold":     { bg: "#FFF4EC", color: "#C4762A" },
  "Completed":   { bg: "#EAF6EA", color: "#217A21" },
  "Closed":      { bg: "#EBEBEB", color: "#444" },
};
const CATEGORY_COLOR = {
  Procurement: "#0066FF", Logistics: "#006E6E", Contracts: "#7B2D8B",
  HSE: "#C4262E", Projects: "#D1521A", General: "#637074",
};

const SEARCH_FIELDS = [
  { value: "all",    label: "All fields" },
  { value: "title",  label: "Title" },
  { value: "vendor", label: "Vendor" },
  { value: "dept",   label: "Department" },
  { value: "staff",  label: "Assigned To" },
  { value: "owner",  label: "Owner" },
  { value: "entity", label: "Entity" },
];
const PAGE_SIZES = [10, 20, 50];

// ── Helpers ────────────────────────────────────────────────────────────────────
function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
}
function daysLeft(dueDate) {
  if (!dueDate) return null;
  return Math.ceil((new Date(dueDate) - new Date()) / 86400000);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ label, count, color, bg, icon: Icon, active, onClick, sub }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, minWidth: 130, position: "relative", overflow: "hidden",
      background: active ? bg : "#fff",
      border: `1.5px solid ${active ? color : "#E4E8EF"}`,
      borderRadius: 14, padding: "16px 18px", cursor: "pointer",
      textAlign: "left", transition: "all 0.16s",
      boxShadow: active ? `0 0 0 3px ${color}22, 0 2px 8px rgba(0,0,0,0.06)` : "0 1px 4px rgba(0,0,0,0.05)",
    }}>
      {active && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: "14px 14px 0 0" }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon style={{ color, fontSize: 18 }} />
        </div>
        <span style={{ fontSize: 30, fontWeight: 800, color: active ? color : "#001A3D", lineHeight: 1 }}>{count}</span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: active ? color : "#888", letterSpacing: "0.6px", display: "block" }}>{label.toUpperCase()}</span>
      {sub && <span style={{ fontSize: 10, color: "#AAA", display: "block", marginTop: 2 }}>{sub}</span>}
    </button>
  );
}

function Chip({ bg, color, text, maxLen = 999 }) {
  const label = text?.length > maxLen ? text.slice(0, maxLen) + "…" : text;
  return (
    <span style={{ background: bg, color, padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
      {label}
    </span>
  );
}

function ActiveFilterChip({ label, onRemove }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "#EBF3FF", color: "#0052A3", border: "1px solid #C0D8F8",
      padding: "3px 8px 3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {label}
      <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color: "#0052A3", padding: 0, lineHeight: 1, fontSize: 14, fontWeight: 700 }}>×</button>
    </span>
  );
}

const iconBtn = (color, hoverBg = "#F5F5F5") => ({
  base: { width: 30, height: 30, border: "1px solid #E4E8EF", borderRadius: 7, background: "#fff", color, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.14s", flexShrink: 0 },
  hover: hoverBg,
});

function ActionBtn({ onClick, color, hoverBg, children, title }) {
  const [hov, setHov] = useState(false);
  const s = iconBtn(color, hoverBg);
  return (
    <Tooltip content={title} relationship="label">
      <button onClick={onClick} title={title}
        style={{ ...s.base, background: hov ? hoverBg : "#fff" }}
        onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
        {children}
      </button>
    </Tooltip>
  );
}

function ActivityCard({ activity, onView, onEdit, onCloseTask, onReopenTask, canEdit }) {
  const catColor = CATEGORY_COLOR[activity.category] || "#637074";
  const ps = PRIORITY_STYLE[activity.priority] || PRIORITY_STYLE.Medium;
  const ss = STATUS_STYLE[activity.status] || STATUS_STYLE["Not Started"];
  const isClosed = activity.status === "Closed";
  const dl = daysLeft(activity.dueDate);
  const isOverdue = dl !== null && dl < 0 && !["Completed", "Closed"].includes(activity.status);
  const latestNote = latestProgressNote(activity);
  const [hov, setHov] = useState(false);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "#fff",
        border: `1.5px solid ${hov ? (isOverdue ? "#E8A0A3" : "#BCC8F8") : (isOverdue ? "#F5C8CB" : "#E4E8EF")}`,
        borderLeft: `4px solid ${isOverdue ? "#C4262E" : catColor}`,
        borderRadius: "0 14px 14px 0",
        display: "flex", flexDirection: "column",
        boxShadow: hov ? "0 6px 24px rgba(0,26,61,0.11)" : "0 1px 4px rgba(0,0,0,0.05)",
        transform: hov ? "translateY(-2px)" : "none",
        transition: "all 0.18s ease",
        overflow: "hidden",
      }}
    >
      {/* Body */}
      <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
            <Chip bg={catColor + "18"} color={catColor} text={activity.category} />
            {activity.userDept && <Chip bg="#EEF1FF" color="#3451B2" text={activity.userDept} maxLen={20} />}
            {activity.entity && activity.entity !== "N/A" && <Chip bg="#EAF6EA" color="#217A21" text={activity.entity} />}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: ps.dot }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: ps.color }}>{activity.priority}</span>
          </div>
        </div>

        <div>
          <span style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, lineHeight: 1.45, display: "block" }}>{activity.title}</span>
          {activity.contractor && (
            <span style={{ fontSize: 11, color: "#888", display: "block", marginTop: 2 }}>
              Vendor: <strong style={{ color: "#555" }}>{activity.contractor}</strong>
            </span>
          )}
        </div>

        {latestNote && (
          <div style={{ background: "#F6F7FA", borderRadius: 8, padding: "8px 10px", borderLeft: "3px solid #D0D7E5" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#AAA", letterSpacing: "0.6px", display: "block", marginBottom: 3 }}>KEY ACTIVITIES</span>
            <span style={{ fontSize: 11, color: "#444", lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{latestNote}</span>
          </div>
        )}

        {activity.lookAhead && (
          <div style={{ background: "#F0F4FF", borderRadius: 8, padding: "8px 10px", borderLeft: "3px solid #B8C8FF" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "#5A78EE", letterSpacing: "0.6px", display: "block", marginBottom: 3 }}>LOOK AHEAD</span>
            <span style={{ fontSize: 11, color: "#444", lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{activity.lookAhead}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: "10px 16px", borderTop: "1px solid #F0F2F5", background: "#FAFBFC" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 9 }}>
          <span style={{ background: ss.bg, color: ss.color, padding: "2px 9px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{activity.status}</span>
          {activity.assignedToName && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 19, height: 19, borderRadius: "50%", background: "#E8EDFF", color: "#3451B2", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {initials(activity.assignedToName)}
              </span>
              <span style={{ fontSize: 10.5, color: "#555" }}>{activity.assignedToName.split(" ")[0]}</span>
            </div>
          )}
          {dl !== null && (
            <span style={{ fontSize: 10, fontWeight: isOverdue ? 700 : 400, color: isOverdue ? "#C4262E" : dl <= 7 ? "#C4762A" : "#999", marginLeft: "auto" }}>
              {isOverdue ? `⚠ ${Math.abs(dl)}d overdue` : dl === 0 ? "Due today" : `Due ${fmtDate(activity.dueDate)}`}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          <button onClick={() => onView(activity)} style={{
            flex: 1, height: 31, border: "1px solid #D8DEEE", borderRadius: 8, background: "#fff", color: "#0066FF",
            fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, transition: "background 0.14s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "#EEF3FF"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
            <Eye24Regular style={{ fontSize: 14 }} /> Details
          </button>
          {canEdit && !isClosed && (
            <>
              <ActionBtn onClick={() => onEdit(activity)} color="#444" hoverBg="#F4F4F6" title="Edit task"><Edit24Regular style={{ fontSize: 15 }} /></ActionBtn>
              <ActionBtn onClick={() => onCloseTask(activity)} color="#888" hoverBg="#FFF0F0" title="Close task"><Archive24Regular style={{ fontSize: 15 }} /></ActionBtn>
            </>
          )}
          {canEdit && isClosed && (
            <ActionBtn onClick={() => onReopenTask(activity)} color="#006E6E" hoverBg="#F0FFFF" title="Reopen task"><ArrowCounterclockwise24Regular style={{ fontSize: 15 }} /></ActionBtn>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ activity, idx, onView, onEdit, onCloseTask, onReopenTask, canEdit }) {
  const catColor = CATEGORY_COLOR[activity.category] || "#637074";
  const ss = STATUS_STYLE[activity.status] || STATUS_STYLE["Not Started"];
  const isClosed = activity.status === "Closed";
  const dl = daysLeft(activity.dueDate);
  const isOverdue = dl !== null && dl < 0 && !["Completed", "Closed"].includes(activity.status);
  const latestNote = latestProgressNote(activity);
  const [hov, setHov] = useState(false);

  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ borderBottom: "1px solid #EEF0F4", background: hov ? (isOverdue ? "#FFF5F5" : "#F7F9FF") : (isOverdue ? "#FFF8F8" : "#fff"), transition: "background 0.12s" }}
    >
      <td style={{ width: 4, padding: 0 }}><div style={{ width: 4, minHeight: 52, background: catColor }} /></td>
      <td style={{ padding: "11px 10px", fontSize: 12, color: "#AAA", textAlign: "center", width: 38 }}>{idx + 1}</td>
      <td style={{ padding: "11px 14px", minWidth: 180, maxWidth: 260 }}>
        <span style={{ fontWeight: 700, color: "#001A3D", fontSize: 12.5, display: "block", lineHeight: 1.4 }}>{activity.title}</span>
        {activity.contractor && <span style={{ fontSize: 10.5, color: "#999", display: "block", marginTop: 1 }}>{activity.contractor}</span>}
      </td>
      <td style={{ padding: "11px 14px", fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: catColor, display: "inline-block", flexShrink: 0 }} />
          {activity.userDept || "—"}
        </div>
      </td>
      <td style={{ padding: "11px 14px", fontSize: 11.5, color: "#555", maxWidth: 220 }}>
        {latestNote ? <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>{latestNote}</span> : <span style={{ color: "#CCC" }}>—</span>}
      </td>
      <td style={{ padding: "11px 14px", fontSize: 11.5, color: "#555", maxWidth: 180 }}>
        {activity.lookAhead ? <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }}>{activity.lookAhead}</span> : <span style={{ color: "#CCC" }}>—</span>}
      </td>
      <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}>
        <span style={{ background: ss.bg, color: ss.color, padding: "3px 9px", borderRadius: 10, fontSize: 10.5, fontWeight: 700 }}>{activity.status}</span>
      </td>
      <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}>
        {activity.assignedToName ? (
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#E8EDFF", color: "#3451B2", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              {initials(activity.assignedToName)}
            </span>
            <span style={{ fontSize: 11, color: "#555" }}>{activity.assignedToName.split(" ")[0]}</span>
          </div>
        ) : <span style={{ color: "#CCC", fontSize: 12 }}>—</span>}
      </td>
      <td style={{ padding: "11px 14px", fontSize: 11, whiteSpace: "nowrap", color: isOverdue ? "#C4262E" : "#888", fontWeight: isOverdue ? 700 : 400 }}>
        {dl !== null ? (isOverdue ? `${Math.abs(dl)}d overdue` : fmtDate(activity.dueDate)) : "—"}
      </td>
      <td style={{ padding: "11px 14px" }}>
        <div style={{ display: "flex", gap: 3 }}>
          <Tooltip content="View details" relationship="label">
            <Button appearance="subtle" size="small" icon={<Eye24Regular />} onClick={() => onView(activity)} style={{ color: "#0066FF" }} />
          </Tooltip>
          {canEdit && !isClosed && (
            <>
              <Tooltip content="Edit" relationship="label">
                <Button appearance="subtle" size="small" icon={<Edit24Regular />} onClick={() => onEdit(activity)} />
              </Tooltip>
              <Tooltip content="Close task" relationship="label">
                <Button appearance="subtle" size="small" icon={<Archive24Regular />} onClick={() => onCloseTask(activity)} style={{ color: "#888" }} />
              </Tooltip>
            </>
          )}
          {canEdit && isClosed && (
            <Tooltip content="Reopen" relationship="label">
              <Button appearance="subtle" size="small" icon={<ArrowCounterclockwise24Regular />} onClick={() => onReopenTask(activity)} style={{ color: "#006E6E" }} />
            </Tooltip>
          )}
        </div>
      </td>
    </tr>
  );
}

function Pagination({ page, totalPages, onPage, pageSize, onPageSize, total, filtered: cnt }) {
  if (total === 0) return null;
  const pages = [];
  const delta = 1;
  let last;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= delta) {
      if (last && i - last > 1) pages.push("…");
      pages.push(i);
      last = i;
    }
  }
  const btnBase = (active) => ({
    minWidth: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${active ? "#0066FF" : "#DDE1EA"}`, borderRadius: 8,
    background: active ? "#0066FF" : "#fff", color: active ? "#fff" : "#444",
    cursor: "pointer", fontSize: 12.5, fontWeight: active ? 700 : 400, padding: "0 8px",
    transition: "all 0.12s",
  });
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 4px 4px", flexWrap: "wrap", gap: 10 }}>
      <span style={{ fontSize: 12, color: "#888" }}>
        {cnt === total ? `${total} task${total !== 1 ? "s" : ""}` : `${cnt} of ${total} tasks`}
        {totalPages > 1 && ` · Page ${page} of ${totalPages}`}
      </span>
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button onClick={() => onPage(page - 1)} disabled={page === 1} style={{ ...btnBase(false), color: page === 1 ? "#CCC" : "#444", borderColor: page === 1 ? "#EEE" : "#DDE1EA", cursor: page === 1 ? "default" : "pointer" }}>‹</button>
          {pages.map((p, i) =>
            p === "…" ? <span key={`e${i}`} style={{ padding: "0 4px", color: "#AAA" }}>…</span>
              : <button key={p} onClick={() => onPage(p)} style={btnBase(p === page)}>{p}</button>
          )}
          <button onClick={() => onPage(page + 1)} disabled={page === totalPages} style={{ ...btnBase(false), color: page === totalPages ? "#CCC" : "#444", borderColor: page === totalPages ? "#EEE" : "#DDE1EA", cursor: page === totalPages ? "default" : "pointer" }}>›</button>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 11, color: "#888" }}>Per page:</span>
        <select value={pageSize} onChange={e => onPageSize(Number(e.target.value))} style={{ border: "1px solid #DDE1EA", borderRadius: 7, padding: "4px 8px", fontSize: 12, color: "#333", background: "#fff", cursor: "pointer", outline: "none" }}>
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function ActivityTrackerPage() {
  const { instance, accounts } = useMsal();
  const { activities, loading, isDemoMode, spError, refresh, addActivity, editActivity, addProgressUpdate, toggleCFOIssue, addCFORecommendation } = useActivityTracker();
  const { canEdit, isAdmin, isEditor, isManager, currentUserEmail, currentUserName } = useUserAccess();
  const { map: deptOwnerMap } = useDeptOwnerMap();
  const canViewAll = isAdmin || isManager || isEditor;

  // View state
  const [viewMode, setViewMode]   = useState("grid");
  const [viewScope, setViewScope] = useState("mine");

  // Filters
  const [search, setSearch]             = useState("");
  const [searchField, setSearchField]   = useState("all");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterUserDept, setFilterUserDept] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");

  // Pagination
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // UI state
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [formOpen, setFormOpen]       = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [closedDialogOpen, setClosedDialogOpen] = useState(false);
  const [closedSearch, setClosedSearch] = useState("");
  const [confirmBackfill, setConfirmBackfill] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillDone, setBackfillDone] = useState(0);
  const [backfillResult, setBackfillResult] = useState(null);

  // Report state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType]   = useState("gm");
  const [weekEnding, setWeekEnding]   = useState(new Date().toISOString().slice(0, 10));
  const [reportSending, setReportSending] = useState(false);
  const [reportResult, setReportResult]   = useState(null);
  const [gmUser, setGmUser]           = useState(null);
  const [showGMPreview, setShowGMPreview] = useState(false);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [search, searchField, filterStatus, filterCategory, filterUserDept, filterPriority, viewScope]);

  // ── Derived data ─────────────────────────────────────────────────────────────
  const visibleActivities = useMemo(() => {
    if (canViewAll) return activities;
    return activities.filter(a => a.assignedTo?.toLowerCase() === currentUserEmail.toLowerCase());
  }, [activities, canViewAll, currentUserEmail]);

  const activeActivities = useMemo(() => visibleActivities.filter(a => a.status !== "Closed"), [visibleActivities]);
  const closedActivities = useMemo(() => visibleActivities.filter(a => a.status === "Closed"), [visibleActivities]);

  const myActivities = useMemo(() =>
    activeActivities.filter(a => a.assignedTo?.toLowerCase() === currentUserEmail.toLowerCase()),
    [activeActivities, currentUserEmail]);

  const displayActivities = useMemo(() => {
    if (!canViewAll || viewScope === "mine") return myActivities;
    return activeActivities;
  }, [canViewAll, viewScope, myActivities, activeActivities]);

  const reportOwnerCount = useMemo(() => {
    const emails = new Set();
    myActivities.forEach(a => {
      const owners = deptOwnerMap[a.userDept];
      if (owners?.length > 0) owners.forEach(o => { if (o.email) emails.add(o.email); });
      else if (a.ownerEmail) emails.add(a.ownerEmail);
    });
    return emails.size;
  }, [myActivities, deptOwnerMap]);

  // Completed work stays completed after it is closed, so the closed archive is
  // counted too — scoped to the same staff view as the active list.
  const scopedClosed = useMemo(() => {
    if (canViewAll && viewScope === "mine") {
      return closedActivities.filter(
        a => a.assignedTo?.toLowerCase() === currentUserEmail.toLowerCase(),
      );
    }
    return closedActivities;
  }, [closedActivities, canViewAll, viewScope, currentUserEmail]);

  const stats = useMemo(() => {
    const activeCompleted = displayActivities.filter(a => a.status === "Completed").length;
    const closedCompleted = scopedClosed.filter(wasCompletedAtClose).length;
    return {
      total:      displayActivities.length,
      inProgress: displayActivities.filter(a => a.status === "In Progress").length,
      activeCompleted,
      closedCompleted,
      completed:  activeCompleted + closedCompleted,
      overdue:    displayActivities.filter(a => a.dueDate && !["Completed", "Closed"].includes(a.status) && new Date(a.dueDate) < new Date()).length,
    };
  }, [displayActivities, scopedClosed]);

  const activityDepts = useMemo(() =>
    [...new Set(displayActivities.map(a => a.userDept).filter(Boolean))].sort(),
    [displayActivities]);

  const filtered = useMemo(() => {
    return displayActivities.filter(a => {
      if (filterStatus   !== "All" && a.status   !== filterStatus)   return false;
      if (filterCategory !== "All" && a.category !== filterCategory) return false;
      if (filterUserDept !== "All" && a.userDept !== filterUserDept) return false;
      if (filterPriority !== "All" && a.priority !== filterPriority) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      switch (searchField) {
        case "title":  return a.title?.toLowerCase().includes(q);
        case "vendor": return a.contractor?.toLowerCase().includes(q);
        case "dept":   return a.userDept?.toLowerCase().includes(q);
        case "staff":  return (a.assignedToName || a.assignedTo)?.toLowerCase().includes(q);
        case "owner":  return a.ownerName?.toLowerCase().includes(q);
        case "entity": return a.entity?.toLowerCase().includes(q);
        default:
          return a.title?.toLowerCase().includes(q) ||
            a.contractor?.toLowerCase().includes(q) ||
            a.userDept?.toLowerCase().includes(q) ||
            (a.assignedToName || a.assignedTo)?.toLowerCase().includes(q) ||
            a.ownerName?.toLowerCase().includes(q) ||
            a.entity?.toLowerCase().includes(q) ||
            a.lookAhead?.toLowerCase().includes(q) ||
            a.description?.toLowerCase().includes(q);
      }
    });
  }, [displayActivities, filterStatus, filterCategory, filterUserDept, filterPriority, search, searchField]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const debouncedClosedSearch = useDebounced(closedSearch, 220);

  const filteredClosedActivities = useMemo(() => {
    if (!debouncedClosedSearch.trim()) return closedActivities;
    const q = debouncedClosedSearch.toLowerCase();
    return closedActivities.filter(a =>
      a.title?.toLowerCase().includes(q) ||
      a.userDept?.toLowerCase().includes(q) ||
      a.contractor?.toLowerCase().includes(q) ||
      (a.assignedToName || a.assignedTo)?.toLowerCase().includes(q)
    );
  }, [closedActivities, debouncedClosedSearch]);

  // The closed archive only ever grows, so it must not render in full.
  // Closed before an outcome was ever recorded — these are the backlog.
  const unrecordedClosed = useMemo(
    () => closedActivities.filter(hasUnrecordedOutcome),
    [closedActivities],
  );

  const closedPager = usePagination(filteredClosedActivities, {
    defaultPageSize: 25,
    resetKey: debouncedClosedSearch,
  });

  const activeFilters = [
    filterStatus   !== "All" && { label: `Status: ${filterStatus}`,     clear: () => setFilterStatus("All") },
    filterCategory !== "All" && { label: `Category: ${filterCategory}`, clear: () => setFilterCategory("All") },
    filterUserDept !== "All" && { label: `Dept: ${filterUserDept}`,     clear: () => setFilterUserDept("All") },
    filterPriority !== "All" && { label: `Priority: ${filterPriority}`, clear: () => setFilterPriority("All") },
    search.trim()            && { label: `"${search.trim().slice(0, 24)}${search.trim().length > 24 ? "…" : ""}"`, clear: () => setSearch("") },
  ].filter(Boolean);

  const hasFilters = activeFilters.length > 0;

  // ── Handlers ─────────────────────────────────────────────────────────────────
  function openCreate() { setEditTarget(null); setFormOpen(true); }
  function openEdit(a)  { setEditTarget(a); setFormOpen(true); setSelectedActivity(null); }

  async function handleSave(data) {
    if (editTarget) {
      await editActivity(editTarget.id, data);
      if (selectedActivity?.id === editTarget.id) setSelectedActivity({ ...editTarget, ...data });
    } else {
      await addActivity(data);
    }
  }

  async function handleCloseTask(a) {
    // Stamp the status being closed from into the trail. Without this the
    // information is lost the moment status becomes "Closed", and a completed
    // task becomes indistinguishable from an abandoned one.
    const closingFrom = a.status || "Not Started";
    const entry = {
      id: `u-${Date.now()}`,
      date: new Date().toISOString(),
      by: currentUserEmail,
      byName: currentUserName,
      status: closingFrom,
      note: `Task closed. Status at close: ${closingFrom}.`,
      lookAhead: "",
      system: true,
    };
    const updated = {
      ...a,
      status: "Closed",
      updates: [entry, ...(a.updates || [])],
    };
    await editActivity(a.id, updated);
    if (selectedActivity?.id === a.id) setSelectedActivity(updated);
  }

  // ── Backfilling outcomes on tasks closed before the outcome was captured ────

  async function handleSetOutcome(a, status) {
    const updated = {
      ...a,
      updates: [outcomeBackfillEntry(status, currentUserEmail, currentUserName), ...(a.updates || [])],
    };
    await editActivity(a.id, updated);
    if (selectedActivity?.id === a.id) setSelectedActivity(updated);
  }

  async function handleBulkBackfill() {
    const targets = unrecordedClosed;
    if (!targets.length) return;
    setBackfilling(true);
    setBackfillDone(0);
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        await handleSetOutcome(targets[i], "Completed");
      } catch {
        failed++;
      }
      setBackfillDone(i + 1);
    }
    setBackfilling(false);
    setConfirmBackfill(false);
    setBackfillResult({ total: targets.length, failed });
  }

  async function handleReopenTask(a) {
    // Restore where it left off rather than pretending it never started.
    const restored = finalStatusOf(a);
    const status = restored === "Closed" ? "Not Started" : restored;
    const entry = {
      id: `u-${Date.now()}`,
      date: new Date().toISOString(),
      by: currentUserEmail,
      byName: currentUserName,
      status,
      note: `Task reopened at "${status}".`,
      lookAhead: "",
      system: true,
    };
    const updated = { ...a, status, updates: [entry, ...(a.updates || [])] };
    await editActivity(a.id, updated);
    if (selectedActivity?.id === a.id) setSelectedActivity(updated);
  }

  async function handleAddProgress(id, update) {
    const result = await addProgressUpdate(id, update);
    if (result && selectedActivity?.id === id) setSelectedActivity(result.activity);
    return result;
  }

  async function handleToggleCFO(id, newValue) {
    const updated = await toggleCFOIssue(id, newValue);
    if (updated && selectedActivity?.id === id) setSelectedActivity(updated);
  }

  async function handleAddCFORecommendation(id, rec) {
    const result = await addCFORecommendation(id, rec);
    if (result && selectedActivity?.id === id) setSelectedActivity(result.activity);
    return result;
  }

  async function handleGenerateGMReport() {
    if (!gmUser?.email) { setReportResult({ type: "gm", success: false, error: "Please select the General Manager." }); return; }
    setReportSending(true); setReportResult(null);
    try {
      const r = await sendGMReport(instance, accounts, activeActivities, gmUser.email, gmUser.name, currentUserName, weekEnding);
      setReportResult({ type: "gm", success: true, ...r });
    } catch (e) { setReportResult({ type: "gm", success: false, error: e.message }); }
    finally { setReportSending(false); }
  }

  async function handleSendUserReports() {
    setReportSending(true); setReportResult(null);
    try {
      const results = await sendUserReports(instance, accounts, myActivities, currentUserName, weekEnding, deptOwnerMap);
      setReportResult({ type: "user", success: true, results });
    } catch (e) { setReportResult({ type: "user", success: false, error: e.message }); }
    finally { setReportSending(false); }
  }

  function clearFilters() { setFilterStatus("All"); setFilterCategory("All"); setFilterUserDept("All"); setFilterPriority("All"); setSearch(""); }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 2px" }}>

      {/* ── HEADER ── */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        marginBottom: 20, gap: 12, flexWrap: "wrap",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg,#0066FF,#002D7A)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TaskListSquareLtr24Regular style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <Text size={600} weight="bold" style={{ color: "#001A3D", display: "block", lineHeight: 1.2 }}>Activity Tracker</Text>
              <Text style={{ color: "#7E8CA0", fontSize: 12.5 }}>
                Log, track and report SCM activities
                {isDemoMode && <Badge appearance="tint" color="warning" style={{ marginLeft: 8, fontSize: 10 }}>Demo Mode</Badge>}
              </Text>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
          <Button appearance="subtle" size="small" icon={<ArrowSync24Regular />} onClick={refresh} disabled={loading}>Refresh</Button>

          <button
            onClick={() => setClosedDialogOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 13px", border: "1px solid #D0D5DF", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, color: "#444", fontWeight: 500, transition: "all 0.14s" }}
            onMouseEnter={e => e.currentTarget.style.background = "#F6F7FA"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
          >
            <Archive24Regular style={{ fontSize: 16, color: "#777" }} />
            Closed
            {closedActivities.length > 0 && (
              <span style={{ background: "#777", color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{closedActivities.length}</span>
            )}
          </button>

          {canEdit && (
            <button
              onClick={() => { setReportType("user"); setReportResult(null); setReportDialogOpen(true); }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 13px", border: "1px solid #006E6E", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, color: "#006E6E", fontWeight: 600, transition: "all 0.14s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#F0FFFF"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              <MailMultiple24Regular style={{ fontSize: 16 }} /> My Report
            </button>
          )}

          {isEditor && (
            <button
              onClick={() => { setReportType("gm"); setReportResult(null); setGmUser(null); setShowGMPreview(false); setReportDialogOpen(true); }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 13px", border: "1px solid #B8430A", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, color: "#B8430A", fontWeight: 600, transition: "all 0.14s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#FFF4EC"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
            >
              <DocumentArrowDown24Regular style={{ fontSize: 16 }} /> GM Report
            </button>
          )}

          {canEdit && (
            <button
              onClick={openCreate}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 15px", border: "none", borderRadius: 8, background: "linear-gradient(135deg,#0066FF,#002D7A)", cursor: "pointer", fontSize: 13, color: "#fff", fontWeight: 600, boxShadow: "0 2px 8px rgba(0,102,255,0.30)", transition: "all 0.14s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              <Add24Regular style={{ fontSize: 16 }} /> New Task
            </button>
          )}
        </div>
      </div>

      {/* SP error banner */}
      {isDemoMode && spError && (
        <div style={{ background: "#FFF4EC", border: "1px solid #F7C59F", borderRadius: 10, padding: "11px 16px", marginBottom: 18, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <Warning24Regular style={{ color: "#E65C00", flexShrink: 0 }} />
          <div>
            <span style={{ fontWeight: 700, color: "#E65C00", fontSize: 13, display: "block" }}>SharePoint unavailable — demo mode</span>
            <span style={{ fontSize: 11.5, color: "#8B4513", display: "block" }}>{spError}</span>
          </div>
          <Button appearance="subtle" size="small" icon={<ArrowSync24Regular />} onClick={refresh} style={{ marginLeft: "auto", color: "#E65C00" }}>Retry</Button>
        </div>
      )}

      {/* ── STAT CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard label="Active Tasks" count={stats.total} color="#0066FF" bg="#EEF3FF" icon={TaskListSquareLtr24Regular}
          active={filterStatus === "All"} onClick={() => setFilterStatus("All")}
          sub={canViewAll && viewScope === "mine" && activeActivities.length > myActivities.length ? `${activeActivities.length} total all staff` : undefined} />
        <StatCard label="In Progress" count={stats.inProgress} color="#0052A3" bg="#EBF3FF" icon={Clock24Regular}
          active={filterStatus === "In Progress"} onClick={() => setFilterStatus(filterStatus === "In Progress" ? "All" : "In Progress")} />
        <StatCard label="Completed" count={stats.completed} color="#217A21" bg="#EAF6EA" icon={CheckmarkCircle24Filled}
          active={filterStatus === "Completed"} onClick={() => setFilterStatus(filterStatus === "Completed" ? "All" : "Completed")}
          sub={stats.closedCompleted > 0
            ? `${stats.activeCompleted} active · ${stats.closedCompleted} closed`
            : undefined} />
        <StatCard label="Overdue" count={stats.overdue} color="#C4262E" bg="#FDE7E9" icon={Warning24Regular}
          active={false} onClick={() => {}} />
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ background: "#fff", border: "1px solid #E4E8EF", borderRadius: 14, padding: "14px 16px", marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>

        {/* Row 1: Search + scope + view toggle */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          {/* Search with field selector */}
          <div style={{ flex: 1, minWidth: 260, display: "flex", border: "1.5px solid #D0D7E8", borderRadius: 10, overflow: "hidden", background: "#fff", transition: "border-color 0.14s" }}
            onFocusCapture={e => e.currentTarget.style.borderColor = "#0066FF"}
            onBlurCapture={e => e.currentTarget.style.borderColor = "#D0D7E8"}
          >
            <select
              value={searchField}
              onChange={e => setSearchField(e.target.value)}
              style={{ padding: "0 10px", height: 38, border: "none", borderRight: "1px solid #E4E8EF", background: "#F5F7FF", fontSize: 11.5, color: "#555", cursor: "pointer", outline: "none", fontWeight: 600, minWidth: 100 }}
            >
              {SEARCH_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <div style={{ display: "flex", alignItems: "center", flex: 1, padding: "0 10px", gap: 6 }}>
              <Search24Regular style={{ color: "#AAB4C8", fontSize: 16, flexShrink: 0 }} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search by ${SEARCH_FIELDS.find(f => f.value === searchField)?.label?.toLowerCase() || "all fields"}…`}
                style={{ border: "none", outline: "none", fontSize: 13, color: "#222", flex: 1, background: "transparent", height: 38 }}
              />
              {search && (
                <button onClick={() => setSearch("")} style={{ border: "none", background: "none", cursor: "pointer", color: "#AAA", padding: "0 2px", fontSize: 18, lineHeight: 1 }}>×</button>
              )}
            </div>
          </div>

          {/* View scope toggle */}
          {canViewAll && (
            <div style={{ display: "flex", border: "1.5px solid #D0D7E8", borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
              {[{ k: "mine", l: "My Activities" }, { k: "all", l: "All Staff" }].map(({ k, l }) => (
                <button key={k} onClick={() => setViewScope(k)} style={{
                  padding: "0 14px", height: 38, border: "none", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  background: viewScope === k ? "#0066FF" : "#F5F7FF",
                  color: viewScope === k ? "#fff" : "#555",
                  borderLeft: k === "all" ? "1px solid #D0D7E8" : "none",
                  transition: "all 0.14s",
                }}>{l}</button>
              ))}
            </div>
          )}

          {/* View mode */}
          <div style={{ display: "flex", border: "1.5px solid #D0D7E8", borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
            {[{ k: "grid", I: Grid24Regular }, { k: "list", I: List24Regular }].map(({ k, I }) => (
              <button key={k} onClick={() => setViewMode(k)} style={{
                width: 38, height: 38, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                background: viewMode === k ? "#0066FF" : "#F5F7FF",
                color: viewMode === k ? "#fff" : "#888",
                borderLeft: k === "list" ? "1px solid #D0D7E8" : "none",
                transition: "all 0.14s",
              }}><I style={{ fontSize: 17 }} /></button>
            ))}
          </div>
        </div>

        {/* Row 2: Filter dropdowns */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Status", value: filterStatus, onChange: setFilterStatus, options: STATUSES },
            { label: "Category", value: filterCategory, onChange: setFilterCategory, options: CATEGORIES },
            { label: "Priority", value: filterPriority, onChange: setFilterPriority, options: PRIORITIES },
          ].map(({ label, value, onChange, options }) => (
            <select key={label} value={value} onChange={e => onChange(e.target.value)} style={{
              padding: "6px 10px", border: `1.5px solid ${value !== "All" ? "#0066FF" : "#D0D7E8"}`,
              borderRadius: 8, fontSize: 12, color: value !== "All" ? "#0066FF" : "#555",
              background: value !== "All" ? "#EEF3FF" : "#fff", cursor: "pointer", outline: "none",
              fontWeight: value !== "All" ? 700 : 400, minWidth: 110,
            }}>
              <option value="All">All {label}s</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}

          <select value={filterUserDept} onChange={e => setFilterUserDept(e.target.value)} style={{
            padding: "6px 10px", border: `1.5px solid ${filterUserDept !== "All" ? "#0066FF" : "#D0D7E8"}`,
            borderRadius: 8, fontSize: 12, color: filterUserDept !== "All" ? "#0066FF" : "#555",
            background: filterUserDept !== "All" ? "#EEF3FF" : "#fff", cursor: "pointer", outline: "none",
            fontWeight: filterUserDept !== "All" ? 700 : 400, minWidth: 140,
          }}>
            <option value="All">All Departments</option>
            {activityDepts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>

          {/* Active filter chips */}
          {activeFilters.map((f, i) => (
            <ActiveFilterChip key={i} label={f.label} onRemove={f.clear} />
          ))}
          {hasFilters && (
            <button onClick={clearFilters} style={{ fontSize: 11.5, color: "#888", background: "none", border: "none", cursor: "pointer", padding: "3px 6px", borderRadius: 6, fontWeight: 600, textDecoration: "underline" }}>
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── CONTENT ── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0", gap: 14 }}>
          <Spinner size="large" />
          <Text style={{ color: "#888", fontSize: 14 }}>Loading activities…</Text>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #E4E8EF", borderRadius: 16, padding: "64px 40px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: "#F0F4FF", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <TaskListSquareLtr24Regular style={{ fontSize: 32, color: "#B8C8FF" }} />
          </div>
          <Text size={500} weight="semibold" style={{ color: "#3A4660", display: "block", marginBottom: 8 }}>
            {displayActivities.length === 0
              ? (viewScope === "mine" ? "No tasks assigned to you" : "No active tasks")
              : "No tasks match your filters"}
          </Text>
          <Text style={{ color: "#99A6B8", fontSize: 13 }}>
            {displayActivities.length === 0
              ? (viewScope === "mine" ? "Tasks assigned to you will appear here." : "Create a task to get started.")
              : "Try adjusting your search terms or filters."}
          </Text>
          {canEdit && displayActivities.length === 0 && (
            <button onClick={openCreate} style={{ marginTop: 20, padding: "9px 20px", border: "none", borderRadius: 9, background: "linear-gradient(135deg,#0066FF,#002D7A)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Add24Regular style={{ fontSize: 16 }} /> Create First Task
            </button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {paginated.map(a => (
              <ActivityCard key={a.id} activity={a} onView={setSelectedActivity} onEdit={openEdit} onCloseTask={handleCloseTask} onReopenTask={handleReopenTask} canEdit={canEdit} />
            ))}
          </div>
          <Pagination page={safePage} totalPages={totalPages} onPage={setPage} pageSize={pageSize} onPageSize={p => { setPageSize(p); setPage(1); }} total={filtered.length} filtered={filtered.length} />
        </>
      ) : (
        <>
          <div style={{ background: "#fff", border: "1px solid #E4E8EF", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "linear-gradient(135deg,#F5F7FF,#EEF2FF)", borderBottom: "2px solid #E0E6F5" }}>
                    <th style={{ width: 4, padding: 0 }} />
                    <th style={{ width: 38, padding: "11px 10px", fontSize: 10, fontWeight: 700, color: "#99A0B8", textAlign: "center" }}>#</th>
                    {["Description", "Dept", "Key Activities", "Look Ahead", "Status", "Assigned To", "Due", "Actions"].map(h => (
                      <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#667082", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>{h.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((a, i) => (
                    <ActivityRow key={a.id} activity={a} idx={(safePage - 1) * pageSize + i} onView={setSelectedActivity} onEdit={openEdit} onCloseTask={handleCloseTask} onReopenTask={handleReopenTask} canEdit={canEdit} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination page={safePage} totalPages={totalPages} onPage={setPage} pageSize={pageSize} onPageSize={p => { setPageSize(p); setPage(1); }} total={filtered.length} filtered={filtered.length} />
        </>
      )}

      {/* ── CLOSED TASKS DIALOG ── */}
      <Dialog open={closedDialogOpen} onOpenChange={(_, d) => { if (!d.open) { setClosedDialogOpen(false); setClosedSearch(""); } }}>
        <DialogSurface style={{ maxWidth: 860, width: "96vw" }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Archive24Regular style={{ color: "#777" }} />
                Closed Tasks
                <span style={{ background: "#EBEBEB", color: "#444", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{closedActivities.length}</span>
              </div>
            </DialogTitle>
            <DialogContent>
              {closedActivities.length > 0 && (
                <div style={{ marginBottom: 12, display: "flex", border: "1.5px solid #D0D7E8", borderRadius: 9, overflow: "hidden", background: "#fff" }}>
                  <div style={{ display: "flex", alignItems: "center", flex: 1, padding: "0 12px", gap: 7 }}>
                    <Search24Regular style={{ color: "#AAB4C8", fontSize: 15, flexShrink: 0 }} />
                    <input type="text" value={closedSearch} onChange={e => setClosedSearch(e.target.value)} placeholder="Search closed tasks…" style={{ border: "none", outline: "none", fontSize: 13, color: "#222", flex: 1, height: 36, background: "transparent" }} />
                    {closedSearch && <button onClick={() => setClosedSearch("")} style={{ border: "none", background: "none", cursor: "pointer", color: "#AAA", fontSize: 18 }}>×</button>}
                  </div>
                </div>
              )}
              {/* Backlog: tasks closed before the outcome was captured */}
              {canEdit && unrecordedClosed.length > 0 && (
                <div style={{ marginBottom: 14, background: "#FFF8EC", border: "1px solid #FFDFA8", borderRadius: 10, padding: "13px 16px" }}>
                  <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                    <Warning24Regular style={{ color: "#C4762A", fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 13, fontWeight: 700, color: "#7A4A00", display: "block", marginBottom: 3 }}>
                        {unrecordedClosed.length} closed task{unrecordedClosed.length === 1 ? "" : "s"} with no recorded outcome
                      </Text>
                      <Text style={{ fontSize: 12.5, color: "#6B4A00", lineHeight: 1.65, display: "block" }}>
                        These were closed before the app started recording the status a task ended on, so they are not counted
                        as completed. Set each one individually below, or mark them all as completed if that is what they were.
                      </Text>

                      {backfilling ? (
                        <div style={{ marginTop: 11 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                            <Text style={{ fontSize: 12, color: "#6B4A00" }}>Recording outcomes…</Text>
                            <Text style={{ fontSize: 12, fontWeight: 700, color: "#7A4A00" }}>{backfillDone} / {unrecordedClosed.length}</Text>
                          </div>
                          <div style={{ height: 7, background: "#F3E4C8", borderRadius: 4, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${(backfillDone / Math.max(1, unrecordedClosed.length)) * 100}%`, background: "linear-gradient(90deg,#C4762A,#E0983F)", borderRadius: 4, transition: "width 0.25s ease" }} />
                          </div>
                        </div>
                      ) : confirmBackfill ? (
                        <div style={{ marginTop: 11, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <Text style={{ fontSize: 12.5, color: "#7A4A00", fontWeight: 600 }}>
                            Mark all {unrecordedClosed.length} as Completed?
                          </Text>
                          <Button appearance="primary" size="small" onClick={handleBulkBackfill} style={{ background: "#C4762A" }}>
                            Yes, mark all completed
                          </Button>
                          <Button appearance="secondary" size="small" onClick={() => setConfirmBackfill(false)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button appearance="primary" size="small" onClick={() => setConfirmBackfill(true)}
                          style={{ background: "#C4762A", marginTop: 11 }}>
                          Mark all {unrecordedClosed.length} as Completed
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {backfillResult && (
                <div style={{ marginBottom: 14, background: "#EAF6EA", border: "1px solid #BFE3BF", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <CheckmarkCircle24Filled style={{ color: "#217A21", fontSize: 18, flexShrink: 0, marginTop: 1 }} />
                  <Text style={{ fontSize: 12.5, color: "#1A5C1A", lineHeight: 1.65, flex: 1 }}>
                    Recorded {backfillResult.total - backfillResult.failed} outcome{backfillResult.total - backfillResult.failed === 1 ? "" : "s"} as Completed
                    {backfillResult.failed > 0 && ` · ${backfillResult.failed} failed to save`}. The Completed count now includes them.
                  </Text>
                  <button onClick={() => setBackfillResult(null)} style={{ border: "none", background: "none", cursor: "pointer", color: "#5A8A5A", fontSize: 17, lineHeight: 1 }}>×</button>
                </div>
              )}

              {filteredClosedActivities.length === 0 ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Archive24Regular style={{ fontSize: 40, color: "#CCC", display: "block", margin: "0 auto 10px" }} />
                  <Text style={{ color: "#999", fontSize: 14 }}>{closedSearch ? "No closed tasks match your search." : "No closed tasks yet."}</Text>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F5F6FA", borderBottom: "1px solid #E0E4EE" }}>
                        {["Description", "Dept", "Key Activities", "Closed At", "Assigned To", "Actions"].map(h => (
                          <th key={h} style={{ padding: "9px 13px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.5px" }}>{h.toUpperCase()}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {closedPager.pageItems.map((a, i) => {
                        const latestNote = latestProgressNote(a);
                        return (
                          <tr key={a.id} style={{ borderBottom: "1px solid #F0F2F5", background: i % 2 === 0 ? "#fff" : "#FAFBFC", opacity: 0.88 }}>
                            <td style={{ padding: "10px 13px", minWidth: 160 }}>
                              <span style={{ fontWeight: 700, color: "#3A3A3A", fontSize: 12, display: "block" }}>{a.title}</span>
                              {a.contractor && <span style={{ fontSize: 11, color: "#AAA" }}>{a.contractor}</span>}
                            </td>
                            <td style={{ padding: "10px 13px", fontSize: 12, color: "#777", whiteSpace: "nowrap" }}>{a.userDept || "—"}</td>
                            <td style={{ padding: "10px 13px", fontSize: 11.5, color: "#666", maxWidth: 240 }}>
                              {latestNote ? <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>{latestNote}</span> : <span style={{ color: "#CCC" }}>—</span>}
                            </td>
                            <td style={{ padding: "10px 13px", whiteSpace: "nowrap" }}>
                              {(() => {
                                const fs = finalStatusOf(a);
                                if (fs === "Closed") {
                                  if (!canEdit) {
                                    return (
                                      <Tooltip content="No progress update was recorded before this task was closed, so the status it ended on is unknown." relationship="label">
                                        <span style={{ fontSize: 11, color: "#AAA", fontStyle: "italic", cursor: "help" }}>Not recorded</span>
                                      </Tooltip>
                                    );
                                  }
                                  return (
                                    <Menu>
                                      <MenuTrigger disableButtonEnhancement>
                                        <button
                                          disabled={backfilling}
                                          style={{
                                            border: "1px dashed #C4762A", background: "#FFF8EC", color: "#A05F1F",
                                            borderRadius: 10, padding: "2px 10px", fontSize: 10.5, fontWeight: 700,
                                            cursor: backfilling ? "default" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                                          }}
                                        >
                                          Set outcome ▾
                                        </button>
                                      </MenuTrigger>
                                      <MenuPopover>
                                        <MenuList>
                                          {["Completed", "In Progress", "On Hold", "Not Started"].map(s => (
                                            <MenuItem key={s} onClick={() => handleSetOutcome(a, s)}>
                                              Closed as {s}
                                            </MenuItem>
                                          ))}
                                        </MenuList>
                                      </MenuPopover>
                                    </Menu>
                                  );
                                }
                                const ss = STATUS_STYLE[fs] || STATUS_STYLE["Not Started"];
                                return (
                                  <span style={{ background: ss.bg, color: ss.color, padding: "2px 9px", borderRadius: 10, fontSize: 10.5, fontWeight: 700 }}>
                                    {fs}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: "10px 13px", whiteSpace: "nowrap" }}>
                              {a.assignedToName ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                  <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#E8EDFF", color: "#3451B2", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                    {initials(a.assignedToName)}
                                  </span>
                                  <span style={{ fontSize: 11.5, color: "#555" }}>{a.assignedToName}</span>
                                </div>
                              ) : <span style={{ color: "#CCC" }}>—</span>}
                            </td>
                            <td style={{ padding: "10px 13px" }}>
                              <div style={{ display: "flex", gap: 4 }}>
                                <Tooltip content="View details" relationship="label">
                                  <Button appearance="subtle" size="small" icon={<Eye24Regular />} onClick={() => { setSelectedActivity(a); setClosedDialogOpen(false); }} style={{ color: "#0066FF" }} />
                                </Tooltip>
                                {canEdit && (
                                  <Tooltip content="Reopen task" relationship="label">
                                    <Button appearance="subtle" size="small" icon={<ArrowCounterclockwise24Regular />} onClick={() => handleReopenTask(a)} style={{ color: "#006E6E" }} />
                                  </Tooltip>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {closedPager.isPaged && (
                    <div style={{ border: "1px solid #EEF0F4", borderRadius: 8, overflow: "hidden", marginTop: 10 }}>
                      <TablePagination {...closedPager} accent="#0066FF" noun="closed task" pageSizes={[25, 50, 100]} />
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Close</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ── REPORT DIALOG ── */}
      <Dialog open={reportDialogOpen} onOpenChange={(_, d) => { if (!d.open) { setReportDialogOpen(false); setReportResult(null); setShowGMPreview(false); } }}>
        <DialogSurface style={{ maxWidth: reportType === "gm" ? 880 : 480, width: "96vw" }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                {reportType === "gm"
                  ? <><DocumentArrowDown24Regular style={{ color: "#B8430A" }} /> GM Activity Report</>
                  : <><MailMultiple24Regular style={{ color: "#006E6E" }} /> Send My Activity Report</>}
              </div>
            </DialogTitle>
            <DialogContent>
              <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>

                {reportType === "gm" && (
                  <>
                    <div style={{ background: "#FFF8F3", border: "1px solid #FAD9C0", borderRadius: 9, padding: "10px 14px", fontSize: 13, color: "#8B4513", lineHeight: 1.6 }}>
                      Sends an Excel report — one sheet per SCM staff member — directly to the General Manager. Closed tasks excluded.
                    </div>

                    <button
                      onClick={() => setShowGMPreview(v => !v)}
                      style={{ display: "flex", alignItems: "center", gap: 8, background: "#F0F4FF", border: "1px solid #C0D0FF", borderRadius: 9, padding: "9px 14px", cursor: "pointer", fontSize: 13, color: "#003399", fontWeight: 600, textAlign: "left" }}
                    >
                      <Eye24Regular style={{ fontSize: 16 }} />
                      {showGMPreview ? "Hide Preview" : "Preview Report"}
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "#7A94CC", fontWeight: 400 }}>
                        {activeActivities.length} activities · {new Set(activeActivities.map(a => a.assignedToName || a.assignedTo)).size} staff
                      </span>
                    </button>

                    {showGMPreview && (() => {
                      const byStaff = {};
                      activeActivities.forEach(a => {
                        const nm = a.assignedToName || a.assignedTo || "Unassigned";
                        if (!byStaff[nm]) byStaff[nm] = [];
                        byStaff[nm].push(a);
                      });
                      return (
                        <div style={{ border: "1px solid #D0DCFF", borderRadius: 10, overflow: "hidden" }}>
                          <div style={{ background: "#F5F8FF", padding: "7px 13px", borderBottom: "1px solid #D0DCFF", fontSize: 11, color: "#667" }}>
                            Preview — what the GM will receive
                          </div>
                          <div style={{ maxHeight: 340, overflowY: "auto" }}>
                            {Object.entries(byStaff).map(([nm, items]) => (
                              <div key={nm}>
                                <div style={{ background: "#001A3D", color: "#fff", padding: "7px 13px", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.5px", position: "sticky", top: 0, zIndex: 1 }}>{nm.toUpperCase()}</div>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                  <thead>
                                    <tr style={{ background: "#EEF3FF", borderBottom: "1px solid #D0DCFF" }}>
                                      {["Description", "Dept", "Vendor", "Key Activities", "Look Ahead", "Status"].map(h => (
                                        <th key={h} style={{ padding: "5px 9px", textAlign: "left", color: "#556", fontWeight: 700, fontSize: 9.5, letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((a, i) => {
                                      const ss = STATUS_STYLE[a.status] || STATUS_STYLE["Not Started"];
                                      return (
                                        <tr key={a.id} style={{ background: i % 2 === 0 ? "#fff" : "#F8F9FF", borderBottom: "1px solid #F0F0F0" }}>
                                          <td style={{ padding: "6px 9px", color: "#001A3D", fontWeight: 600, minWidth: 120, maxWidth: 160, wordBreak: "break-word" }}>{a.title}</td>
                                          <td style={{ padding: "6px 9px", color: "#666", whiteSpace: "nowrap" }}>{a.userDept || "—"}</td>
                                          <td style={{ padding: "6px 9px", color: "#666", whiteSpace: "nowrap" }}>{a.contractor || "—"}</td>
                                          <td style={{ padding: "6px 9px", color: "#333", maxWidth: 180, wordBreak: "break-word" }}>{latestProgressNote(a) || "—"}</td>
                                          <td style={{ padding: "6px 9px", color: "#333", maxWidth: 150, wordBreak: "break-word" }}>{a.lookAhead || a.updates?.[0]?.lookAhead || "—"}</td>
                                          <td style={{ padding: "6px 9px" }}><span style={{ background: ss.bg, color: ss.color, padding: "2px 7px", borderRadius: 8, fontSize: 9.5, fontWeight: 700 }}>{a.status}</span></td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    <Field label="General Manager" required>
                      <PeoplePicker value={gmUser} onChange={setGmUser} placeholder="Search for General Manager…" />
                    </Field>
                  </>
                )}

                {reportType === "user" && (
                  <>
                    <div style={{ background: "#F0FFFC", border: "1px solid #B0E8DC", borderRadius: 9, padding: "10px 14px", fontSize: 13, color: "#004D40", lineHeight: 1.6 }}>
                      Sends your active tasks to all mapped department owners.{" "}
                      <strong>{reportOwnerCount} owner{reportOwnerCount !== 1 ? "s" : ""}</strong> will receive a report.
                    </div>
                    {myActivities.length === 0 && (
                      <div style={{ background: "#FFF4EC", border: "1px solid #FAD19C", borderRadius: 9, padding: "10px 14px", fontSize: 13, color: "#8B4513" }}>
                        You have no active tasks assigned to you. Nothing will be sent.
                      </div>
                    )}
                    {myActivities.length > 0 && (
                      <div style={{ background: "#F8F9FC", border: "1px solid #E4E8EF", borderRadius: 9, overflow: "hidden" }}>
                        <div style={{ background: "#EEF2FF", padding: "7px 13px", borderBottom: "1px solid #E0E6F5", fontSize: 11, fontWeight: 700, color: "#556", letterSpacing: "0.4px" }}>
                          YOUR TASKS ({myActivities.length})
                        </div>
                        <div style={{ maxHeight: 220, overflowY: "auto" }}>
                          {myActivities.map((a, i) => (
                            <div key={a.id} style={{ padding: "9px 13px", borderBottom: "1px solid #F0F2F5", background: i % 2 === 0 ? "#fff" : "#FAFBFC", display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ width: 7, height: 7, borderRadius: "50%", background: CATEGORY_COLOR[a.category] || "#888", flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: "#001A3D", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</span>
                                {a.userDept && <span style={{ fontSize: 10.5, color: "#888" }}>{a.userDept}</span>}
                              </div>
                              <span style={{ background: STATUS_STYLE[a.status]?.bg || "#eee", color: STATUS_STYLE[a.status]?.color || "#555", padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{a.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <Field label="Week Ending Date" required>
                  <Input type="date" value={weekEnding} onChange={(_, d) => setWeekEnding(d.value)} />
                </Field>

                <div style={{ background: "#F5F6FA", borderRadius: 9, padding: "10px 14px", fontSize: 12, color: "#667" }}>
                  {reportType === "gm" ? (
                    <span><strong>Includes:</strong> {activeActivities.length} active tasks across {new Set(activeActivities.map(a => a.assignedToName || a.assignedTo)).size} staff. Closed tasks excluded.</span>
                  ) : (
                    <span><strong>Sending:</strong> {myActivities.length} task(s) to {reportOwnerCount} owner{reportOwnerCount !== 1 ? "s" : ""}. Closed tasks excluded.</span>
                  )}
                </div>

                {reportResult && (
                  <div style={{ padding: "11px 14px", borderRadius: 9, fontSize: 13, background: reportResult.success ? "#EAF6EA" : "#FDE7E9", color: reportResult.success ? "#1A6B1A" : "#C4262E", border: `1px solid ${reportResult.success ? "#B0DDB0" : "#F5B8B8"}` }}>
                    {reportResult.type === "gm" && reportResult.success && <span>Sent to <strong>{reportResult.gmName || reportResult.gmEmail}</strong>. Attachment: <strong>{reportResult.filename}</strong></span>}
                    {reportResult.type === "user" && reportResult.success && (
                      <span>
                        {reportResult.results.filter(r => r.method === "graph").length} of {reportResult.results.length} email(s) sent successfully.
                        {reportResult.results.filter(r => r.method === "failed").length > 0 && <span style={{ color: "#C4762A" }}> {reportResult.results.filter(r => r.method === "failed").length} failed.</span>}
                      </span>
                    )}
                    {!reportResult.success && <span>Error: {reportResult.error}</span>}
                  </div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={reportSending}>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                disabled={reportSending || !weekEnding || (reportType === "user" && myActivities.length === 0)}
                onClick={reportType === "gm" ? handleGenerateGMReport : handleSendUserReports}
                style={{ background: reportType === "gm" ? "linear-gradient(135deg,#B8430A,#7A2B06)" : "linear-gradient(135deg,#006E6E,#004040)" }}
              >
                {reportSending ? <Spinner size="tiny" /> : reportType === "gm" ? "Send to GM" : "Send My Report"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Form */}
      <ActivityForm open={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave} initial={editTarget} currentUserEmail={currentUserEmail} currentUserName={currentUserName} />

      {/* Detail Panel */}
      {selectedActivity && (
        <ActivityDetailPanel
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
          onEdit={openEdit}
          onAddProgress={handleAddProgress}
          onCloseTask={handleCloseTask}
          onReopenTask={handleReopenTask}
          canEdit={canEdit}
          onToggleCFO={handleToggleCFO}
          onAddCFORecommendation={handleAddCFORecommendation}
        />
      )}
    </div>
  );
}
